/**
 * @module features/navigation/recent-items
 * @description Manages recent items history with localStorage persistence
 */

import { logger } from '../../utils/logger.js';
import { ENTITY_TYPES } from '../entity/entity-types.js';

// Constants
const STORAGE_KEY = 'insightly-archive-recent-items';
const MAX_ITEMS = 10;
const EXPIRY_DAYS = 30;
const EXPIRY_MS = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

/**
 * Add an item to recent items
 * @param {string} entityType - Entity type (projects, opportunities, leads, contacts)
 * @param {string} entityId - Entity ID
 * @param {string} entityName - Entity name
 */
export function addRecentItem(entityType, entityId, entityName) {
    try {
        // Validate inputs - don't save invalid entries
        if (!entityId || entityId === 'undefined' || !entityName || !entityType) {
            logger.warn('Attempted to add invalid recent item:', { entityType, entityId, entityName });
            return;
        }

        const items = getRecentItems();

        // Remove existing entry for this entity
        const filtered = items.filter(item =>
            !(item.type === entityType && item.id === entityId)
        );

        // Add to front
        filtered.unshift({
            type: entityType,
            id: entityId,
            name: entityName,
            timestamp: Date.now()
        });

        // Limit to MAX_ITEMS
        const trimmed = filtered.slice(0, MAX_ITEMS);

        // Save to localStorage
        localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));

        logger.debug(`Added recent item: ${entityType}/${entityId}`);

        // Update display if on selection page
        updateRecentItemsDisplay();

    } catch (error) {
        logger.error('Error adding recent item:', error);
    }
}

/**
 * Get all recent items (with expiry filter)
 * @returns {Array} Array of recent item objects
 */
export function getRecentItems() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return [];

        const items = JSON.parse(stored);
        const now = Date.now();

        // Filter expired items AND invalid items
        return items.filter(item => {
            const notExpired = (now - item.timestamp) < EXPIRY_MS;
            const hasValidId = item.id && item.id !== 'undefined' && item.id.trim() !== '';
            const hasValidType = item.type && ENTITY_TYPES[item.type];
            return notExpired && hasValidId && hasValidType;
        });

    } catch (error) {
        logger.error('Error getting recent items:', error);
        return [];
    }
}

/**
 * Clear all recent items
 */
export function clearRecentItems() {
    try {
        localStorage.removeItem(STORAGE_KEY);
        updateRecentItemsDisplay();
        logger.debug('Recent items cleared');
    } catch (error) {
        logger.error('Error clearing recent items:', error);
    }
}

/**
 * Group recent items by time period
 * @param {Array} items - Array of recent items
 * @returns {Object} Grouped items by time period
 */
function groupItemsByTime(items) {
    const now = Date.now();
    const oneDayMs = 24 * 60 * 60 * 1000;

    const groups = {
        'Today': [],
        'Yesterday': [],
        'This Week': [],
        'Older': []
    };

    items.forEach(item => {
        const age = now - item.timestamp;

        if (age < oneDayMs) {
            groups['Today'].push(item);
        } else if (age < 2 * oneDayMs) {
            groups['Yesterday'].push(item);
        } else if (age < 7 * oneDayMs) {
            groups['This Week'].push(item);
        } else {
            groups['Older'].push(item);
        }
    });

    // Remove empty groups
    Object.keys(groups).forEach(key => {
        if (groups[key].length === 0) delete groups[key];
    });

    return groups;
}

/**
 * Format time for display
 * @param {number} timestamp - Timestamp in milliseconds
 * @returns {string} Formatted time string
 */
function formatTimeAgo(timestamp) {
    const now = Date.now();
    const diff = now - timestamp;

    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    return new Date(timestamp).toLocaleDateString();
}

/**
 * Display recent items on the home page
 */
export function displayRecentItems() {
    const container = document.getElementById('recent-items-section');
    if (!container) return;

    const items = getRecentItems();

    if (items.length === 0) {
        container.classList.add('hidden');
        container.innerHTML = '';
        return;
    }

    // Group items by time
    const grouped = groupItemsByTime(items);

    let html = `
        <div class="recent-items-header">
            <h3>Recent Items</h3>
            <button id="clear-recent-btn" class="btn-link">Clear All</button>
        </div>
    `;

    Object.entries(grouped).forEach(([timeGroup, groupItems]) => {
        html += `
            <div class="recent-items-group">
                <h4>${timeGroup}</h4>
                <div class="recent-items-list">
        `;

        groupItems.forEach(item => {
            const config = ENTITY_TYPES[item.type];
            if (!config) return;

            const icon = config.icon || '📄';
            const label = config.label || item.type;
            const timeAgo = formatTimeAgo(item.timestamp);

            html += `
                <a href="?${config.paramKey}=${encodeURIComponent(item.id)}"
                   class="recent-item-card"
                   data-entity-type="${item.type}"
                   data-entity-id="${item.id}">
                    <div class="recent-item-icon">${icon}</div>
                    <div class="recent-item-content">
                        <div class="recent-item-name">${escapeHtml(item.name)}</div>
                        <div class="recent-item-meta">
                            <span class="recent-item-type">${label}</span>
                            <span class="recent-item-time">${timeAgo}</span>
                        </div>
                    </div>
                </a>
            `;
        });

        html += `
                </div>
            </div>
        `;
    });

    container.innerHTML = html;
    container.classList.remove('hidden');

    // Add clear button handler
    const clearBtn = document.getElementById('clear-recent-btn');
    if (clearBtn) {
        clearBtn.addEventListener('click', (e) => {
            e.preventDefault();
            // eslint-disable-next-line no-alert -- User confirmation for data deletion
            if (confirm('Clear all recent items?')) {
                clearRecentItems();
            }
        });
    }
}

/**
 * Update recent items display (if on selection page)
 */
function updateRecentItemsDisplay() {
    const container = document.getElementById('recent-items-section');
    const selectionSection = document.getElementById('project-selection-section');

    // Only update if we're on the selection page
    if (container && selectionSection && !selectionSection.classList.contains('hidden')) {
        displayRecentItems();
    }
}

/**
 * Escape HTML to prevent XSS
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Initialize recent items functionality
 */
export function initializeRecentItems() {
    // Display recent items when on selection page
    displayRecentItems();

    logger.debug('Recent items initialized');
}

export default {
    addRecentItem,
    getRecentItems,
    clearRecentItems,
    displayRecentItems,
    initializeRecentItems
};
